import React, { useMemo } from 'react'
import cx from 'classnames'
import { Loader, l, Icon, Checkbox, useRefCallback } from '@manychat/manyui'

import { FieldGroupId, FieldType } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import FieldPicker from 'common/fields/linking/components/FieldPicker'
import { useFilterFields } from 'common/filter'
import { Field as IFilterField } from 'common/filter/interfaces'
import { FilterField } from 'common/filter/models/AudienceFilter/constants'
import { TagsPicker } from 'common/tags/components/TagPicker/TagsPicker'
import { Chip } from 'components/Chip'
import LabelTooltip from 'components/LabelTooltip'

import { InitialMigrateStepContentProps } from './interfaces'
import { useInitialStep } from './useInitialStep'

import cm from './Initial.module.css'

export const CustomFieldIconByType: Partial<Record<FieldType, Icon.Icon>> = {
  [FieldType.NUMBER]: Icon.CufNumber,
  [FieldType.TEXT]: Icon.CufText,
  [FieldType.DATE]: Icon.Time,
  [FieldType.DATETIME]: Icon.Time,
  [FieldType.BOOLEAN]: Icon.CufBoolean,
  [FieldType.ARRAY]: Icon.CufArray,
}

const AVAILABLE_SYSTEM_FIELDS = [FilterField.PHONE, FilterField.EMAIL]

const cufFilter = (field: IFilterField) => [FieldType.NUMBER, FieldType.TEXT].includes(field.type)

const makeField = (field: IFilterField): Field => {
  return {
    id: field?.name,
    caption: field?.caption,
    type: field?.type,
    group: FieldGroupId.TAG,
    folderId: field?.folder_id,
    segmentType: field?.segment_type,
  }
}

const isSystemFieldFilter = (field: IFilterField) =>
  AVAILABLE_SYSTEM_FIELDS.includes(field.name as FilterField)

export const InitialMigrateStepContent = (props: InitialMigrateStepContentProps) => {
  const {
    handleTagChange,
    handleCUFChange,
    handleSFChange,
    clearCUFValue,
    clearSFValue,
    handleOverwriteChange,
    handleDeleteTag,
  } = useInitialStep(props)

  const { cufId, sfName, overwrite, tags } = props
  const excluded = useMemo(() => tags.map((tag) => ({ tag_id: tag.value })), [tags])
  const filterFields = useFilterFields()

  const renderFilter = useRefCallback((children: JSX.Element) => (
    <>
      {!filterFields.isLoaded && (
        <div className="p-y p-x-xl">
          <Loader />
        </div>
      )}

      {filterFields.isLoaded && children}
    </>
  ))

  return (
    <div className={cm.modalBody}>
      <h2 className="text-secondary text-center m-b-md">
        {l.translate(
          'You can only export Email and Phone number. Only non-empty fields will be processed.',
        )}
      </h2>

      <div className="d-flex align-center m-b-md">
        <Checkbox
          label={l.translate('Replace data in non-empty System Field')}
          checked={overwrite}
          onChange={handleOverwriteChange}
          infoText={l.translate(
            'Check the box if you want the existing data in a System Field to be replaced by the data in a Custom User Field',
          )}
        />
      </div>

      <div className="d-flex justify-between m-b-xs">
        <h3 className={cm.col}>{l.translate('Custom Field')}</h3>
        <h3 className={cx('m-0', cm.col)}>{l.translate('System Field')}</h3>
      </div>

      <div className="d-flex justify-between align-center m-b-md">
        <div className={cm.col}>
          {renderFilter(
            <div className="p-relative d-flex flex-col align-stretch align-center justify-end">
              {Boolean(cufId) && (
                <Icon.Close size={24} className={cm.deleteIcon} onClick={clearCUFValue} />
              )}
              <FieldPicker
                placeholder={l.translate('Enter field name')}
                data-test-id="cuf-to-suf-cuf"
                fields={filterFields.custom.filter(cufFilter).map(makeField)}
                onSelect={handleCUFChange}
                allowNew={FieldGroupId.USER}
                value={cufId}
                showIcon={false}
                ariaLabel={l.translate('Enter field name')}
              />
            </div>,
          )}
        </div>
        <Icon.OrphanLongArrow
          size={24}
          className={cx(sfName && cufId ? 'text-primary' : 'text-secondary')}
        />
        <div className={cm.col}>
          {renderFilter(
            <div className="p-relative d-flex flex-col align-stretch align-center justify-end">
              {Boolean(sfName) && (
                <Icon.Close size={24} className={cm.deleteIcon} onClick={clearSFValue} />
              )}
              <FieldPicker
                placeholder="Enter field name"
                data-test-id="cuf-to-suf-cuf"
                fields={filterFields.system.filter(isSystemFieldFilter).map(makeField)}
                onSelect={handleSFChange}
                value={sfName}
                showIcon={false}
                ariaLabel={l.translate('Enter field name')}
              />
            </div>,
          )}
        </div>
      </div>

      <div className="d-flex flex-col">
        <div className="d-flex m-b-xs">
          <h3>{l.translate('Tag for contacts with export error (Optional)')}</h3>
          <LabelTooltip className="m-t-xxs">
            {l.translate(
              'Choose a tag that will be applied to contacts with export error. If not set, they will be tagged automatically.',
            )}
          </LabelTooltip>
        </div>
        <TagsPicker excluded={excluded} onChange={handleTagChange} />
        <div className="m-t">
          {tags.map((tag) => (
            <Chip key={tag.value} deletable onDelete={() => handleDeleteTag(tag.value)}>
              {tag.name}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  )
}
